// src/features/dashboard/components/EmployeeLoggedHoursReport/MyHoursVsTeamAverage.tsx

import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';

import {
  MeVsTeamLoggedResponse,
} from '../../types/Employee/employeeLoggedHours.types';

interface Props {
  data?: MeVsTeamLoggedResponse | null;
}

type ActiveLegend =
  | 'me'
  | 'team'
  | 'best'
  | null;

/* ============================================================
 * DAY LABEL
 * ============================================================ */

const getDayLabel = (
  dayName?: string,
  workDate?: string,
): string => {
  const day =
    dayName?.substring(0, 3) ?? '';

  if (!workDate) {
    return day;
  }

  const dateOnly =
    workDate.split('T')[0];

  const parts =
    dateOnly.split('-');

  const dayNumber =
    parts[2] ?? '';

  if (!dayNumber) {
    return day;
  }

  return `${day} ${dayNumber}`;
};

/* ============================================================
 * X AXIS TICK
 *
 * Normal:
 *     Mon 17
 *     Tue 18
 *
 * Hover:
 *          ┌─────────┐
 *          │ Tue 18  │
 *          └─────────┘
 *              ▲
 *
 * The selected box appears only for the hovered point.
 * ============================================================ */

interface CustomXAxisTickProps {
  x?: number;
  y?: number;
  payload?: {
    value?: string;
    index?: number;
  };
  activeIndex?: number | null;
}

const CustomXAxisTick = ({
  x = 0,
  y = 0,
  payload,
  activeIndex,
}: CustomXAxisTickProps) => {
  if (!payload) {
    return null;
  }

  const value =
    payload.value ?? '';

  const index =
    payload.index ?? -1;

  const isActive =
    activeIndex !== null &&
    activeIndex !== undefined &&
    index === activeIndex;

  return (
    <g
      transform={`translate(${x},${y})`}
      pointerEvents="none"
      style={{
        overflow: 'visible',
      }}
    >
      {isActive ? (
        <>
          {/* ====================================================
              EXACT HOVER DESIGN

                 ┌─────────┐
                 │   THU   │
                 └─────────┘
                     ▲
                     │
              The pointer is attached directly to the
              top center of the box.
          ==================================================== */}

          {/* White pointer fill */}
          <path
            d="
              M -7 5
              L 0 -2
              L 7 5
              Z
            "
            fill="#FFFFFF"
            stroke="#98A2B3"
            strokeWidth={1}
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />

          {/* Hide the lower pointer border where it
              meets the box. */}
          <rect
            x="-8"
            y="3"
            width="16"
            height="5"
            fill="#FFFFFF"
          />

          {/* Box */}
          <rect
            x="-23"
            y="5"
            width="46"
            height="38"
            rx="2"
            fill="#FFFFFF"
            stroke="#98A2B3"
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
          />

          {/* Day */}
          <text
            x="0"
            y="25"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#344054"
            fontSize={10}
            fontWeight={400}
          >
            {value
              .substring(0, 3)
              .toUpperCase()}
          </text>
        </>
      ) : (
        <text
          x="0"
          y="20"
          textAnchor="middle"
          dominantBaseline="middle"
          fill="#344054"
          fontSize={13}
          fontWeight={500}
        >
          {value}
        </text>
      )}
    </g>
  );
};

/* ============================================================
 * COMPONENT
 * ============================================================ */




const MyHoursVsTeamAverage = ({
  data,
}: Props) => {
  /* ==========================================================
   * EMPLOYEE / MANAGER NAMES
   * ========================================================== */

  const employeeName =
    data?.managerInfo?.employeeName ??
    sessionStorage.getItem(
      'employeeName',
    ) ??
    localStorage.getItem(
      'employeeName',
    ) ??
    'Me';

  const managerName =
    data?.managerInfo?.managerName ??
    'Team';

  /* ==========================================================
   * CHART DATA
   * ========================================================== */

  const chartData = useMemo(() => {
    if (
      !Array.isArray(data?.trend)
    ) {
      return [];
    }

    return data.trend.map(
      (item) => ({
        day: getDayLabel(
          item.dayName,
          item.workDate,
        ),

        me:
          Number(
            item.myLoggedHours,
          ) || 0,

        team:
          Number(
            item.teamAvgLoggedHours,
          ) || 0,

        best:
          Number(
            item.teamBestLoggedHours,
          ) || 0,

        dayName:
          item.dayName,

        workDate:
          item.workDate,
      }),
    );
  }, [data]);

  /* ==========================================================
   * ZOOM
   * ========================================================== */

  const [
    zoom,
    setZoom,
  ] = useState(
    chartData.length,
  );

  /* ==========================================================
   * LEGEND
   * ========================================================== */

  const [
    activeLegend,
    setActiveLegend,
  ] = useState<ActiveLegend>(
    null,
  );

  /* ==========================================================
   * ACTIVE / HOVER DAY
   * ========================================================== */

  const [
    activeXAxisIndex,
    setActiveXAxisIndex,
  ] = useState<number | null>(
    null,
  );

  /* ==========================================================
   * CHART REF
   * ========================================================== */

  const chartRef =
    useRef<HTMLDivElement | null>(
      null,
    );

  /* ==========================================================
   * RESET
   * ========================================================== */

  useEffect(() => {
    setZoom(chartData.length);
    setActiveXAxisIndex(null);
  }, [chartData.length]);

  /* ==========================================================
   * MOUSE WHEEL ZOOM
   *
   * Prevents the whole page from scrolling while the mouse
   * is over the chart.
   * ========================================================== */

  useEffect(() => {
    const element =
      chartRef.current;

    if (!element) {
      return;
    }

    const handleWheel = (
      event: WheelEvent,
    ) => {
      event.preventDefault();

      if (chartData.length <= 2) {
        return;
      }

      if (event.deltaY < 0) {
        setZoom((current) =>
          Math.max(
            2,
            current - 1,
          ),
        );
      }

      if (event.deltaY > 0) {
        setZoom((current) =>
          Math.min(
            chartData.length,
            current + 1,
          ),
        );
      }
    };

    element.addEventListener(
      'wheel',
      handleWheel,
      {
        passive: false,
      },
    );

    return () => {
      element.removeEventListener(
        'wheel',
        handleWheel,
      );
    };
  }, [chartData.length]);

  /* ==========================================================
   * VISIBLE DATA
   * ========================================================== */

  const visibleData = useMemo(() => {
    if (
      chartData.length === 0
    ) {
      return [];
    }

    return chartData.slice(
      Math.max(
        0,
        chartData.length - zoom,
      ),
    );
  }, [
    chartData,
    zoom,
  ]);

  /* ==========================================================
   * DYNAMIC Y AXIS
   *
   * Always starts from 0h.
   * Tick gap = 3h.
   * ========================================================== */

  const {
    yAxisMax,
    yAxisTicks,
  } = useMemo(() => {
    const values =
      visibleData.flatMap(
        (item) => [
          Number(item.me) || 0,
          Number(item.team) || 0,
          Number(item.best) || 0,
        ],
      );

    const maxValue =
      Math.max(
        ...values,
        0,
      );

    const max =
      Math.max(
        3,
        Math.ceil(
          maxValue / 3,
        ) * 3,
      );

    const ticks =
      Array.from(
        {
          length:
            max / 3 + 1,
        },
        (_, index) =>
          index * 3,
      );

    return {
      yAxisMax: max,
      yAxisTicks: ticks,
    };
  }, [visibleData]);

  /* ==========================================================
   * EMPTY DATA
   * ========================================================== */

  if (
    !data ||
    chartData.length === 0
  ) {
    return (
      <div
        className="
          w-full
          rounded-[20px]
          border
          border-[#D0D5DD]
          bg-white
          px-6
          py-5
          shadow-sm
        "
      >
        <div
          className="
            flex
            items-center
            justify-between
          "
        >
          <h3
            className="
              text-[18px]
              font-semibold
              text-[#002B5B]
            "
          >
            My Hours vs Team Average
          </h3>

          <span
            className="
              rounded-full
              bg-[#EEF4FF]
              px-3
              py-1
              text-[11px]
              font-medium
              text-[#2563EB]
            "
          >
            Daily comparison · This Week ·{' '}
            {managerName}
          </span>
        </div>

        <div
          className="
            flex
            h-[220px]
            items-center
            justify-center
          "
        >
          <span
            className="
              text-[13px]
              text-[#98A2B3]
            "
          >
            No data available
          </span>
        </div>
      </div>
    );
  }

  /* ==========================================================
   * CUSTOM TOOLTIP
   * ========================================================== */

  const CustomTooltip = ({
    active,
    payload,
  }: any) => {
    if (
      !active ||
      !payload?.length
    ) {
      return null;
    }

    const item =
      payload[0]?.payload;

    if (!item) {
      return null;
    }

    return (
      <div
        className="
          pointer-events-none
          min-w-[168px]
          overflow-hidden
          rounded-[8px]
          border
          border-[#D0D5DD]
          bg-white
          shadow-[0_4px_12px_rgba(16,24,40,0.12)]
        "
      >
        {/* Header */}

        <div
          className="
            border-b
            border-[#EAECF0]
            bg-[#F8FAFC]
            px-3
            py-2
          "
        >
          <span
            className="
              text-[12px]
              font-medium
              text-[#344054]
            "
          >
            {item.day}
          </span>
        </div>

        {/* Me */}

        <div
          className="
            flex
            items-center
            gap-2
            px-3
            py-[7px]
          "
        >
          <span
            className="
              h-[11px]
              w-[11px]
              rounded-full
              bg-[#003F7D]
            "
          />

          <span
            className="
              text-[12px]
              text-[#344054]
            "
          >
            Me
          </span>

          <span
            className="
              ml-auto
              text-[12px]
              font-semibold
              text-[#101828]
            "
          >
            {Number(
              item.me,
            ).toFixed(2)}
            h
          </span>
        </div>

        {/* Team Average */}

        <div
          className="
            flex
            items-center
            gap-2
            px-3
            py-[7px]
          "
        >
          <span
            className="
              h-[11px]
              w-[11px]
              rounded-full
              bg-[#98A2B3]
            "
          />

          <span
            className="
              text-[12px]
              text-[#344054]
            "
          >
            Team Avg
          </span>

          <span
            className="
              ml-auto
              text-[12px]
              font-semibold
              text-[#101828]
            "
          >
            {Number(
              item.team,
            ).toFixed(1)}
            h
          </span>
        </div>

        {/* Team Best */}

        <div
          className="
            flex
            items-center
            gap-2
            px-3
            py-[7px]
          "
        >
          <span
            className="
              h-[11px]
              w-[11px]
              rounded-full
              bg-[#16A34A]
            "
          />

          <span
            className="
              text-[12px]
              text-[#344054]
            "
          >
            Team Best
          </span>

          <span
            className="
              ml-auto
              text-[12px]
              font-semibold
              text-[#101828]
            "
          >
            {Number(
              item.best,
            ).toFixed(1)}
            h
          </span>
        </div>
      </div>
    );
  };

  /* ==========================================================
   * RENDER
   * ========================================================== */

  return (
    <div
      className="
        w-full
        rounded-[20px]
        border
        border-[#D0D5DD]
        bg-white
        px-6
        py-5
        shadow-sm
      "
    >
      {/* ======================================================
          HEADER
      ====================================================== */}

      <div
        className="
          mb-5
          flex
          flex-wrap
          items-center
          justify-between
          gap-2
        "
      >
        <h3
          className="
            text-[18px]
            font-semibold
            text-[#002B5B]
          "
        >
          My Hours vs Team Average
        </h3>

        <span
          className="
            rounded-full
            bg-[#EEF4FF]
            px-3
            py-1
            text-[11px]
            font-medium
            text-[#2563EB]
          "
        >
          Daily comparison · This Week ·{' '}
          {managerName}
        </span>
      </div>

      {/* ======================================================
          LEGEND
      ====================================================== */}

      <div
        className="
          mb-4
          flex
          flex-wrap
          items-center
          justify-center
          gap-x-8
          gap-y-2
          text-[14px]
        "
      >
        {/* Me */}

        <div
          className="
            flex
            cursor-pointer
            items-center
            gap-2
          "
          onMouseEnter={() =>
            setActiveLegend('me')
          }
          onMouseLeave={() =>
            setActiveLegend(null)
          }
        >
          <span
            className="
              h-3
              w-3
              rounded-full
              bg-[#003F7D]
            "
            style={{
              opacity:
                activeLegend === null ||
                activeLegend === 'me'
                  ? 1
                  : 0.25,
            }}
          />

          <span
            style={{
              opacity:
                activeLegend === null ||
                activeLegend === 'me'
                  ? 1
                  : 0.4,
            }}
          >
            Me ({employeeName})
          </span>
        </div>

        {/* Team Average */}

        <div
          className="
            flex
            cursor-pointer
            items-center
            gap-2
          "
          onMouseEnter={() =>
            setActiveLegend('team')
          }
          onMouseLeave={() =>
            setActiveLegend(null)
          }
        >
          <span
            className="
              h-3
              w-3
              rounded-full
              bg-[#98A2B3]
            "
            style={{
              opacity:
                activeLegend === null ||
                activeLegend === 'team'
                  ? 1
                  : 0.25,
            }}
          />

          <span
            style={{
              opacity:
                activeLegend === null ||
                activeLegend === 'team'
                  ? 1
                  : 0.4,
            }}
          >
            Team Avg
          </span>
        </div>

        {/* Team Best */}

        <div
          className="
            flex
            cursor-pointer
            items-center
            gap-2
          "
          onMouseEnter={() =>
            setActiveLegend('best')
          }
          onMouseLeave={() =>
            setActiveLegend(null)
          }
        >
          <span
            className="
              h-3
              w-3
              rounded-full
              bg-[#16A34A]
            "
            style={{
              opacity:
                activeLegend === null ||
                activeLegend === 'best'
                  ? 1
                  : 0.25,
            }}
          />

          <span
            style={{
              opacity:
                activeLegend === null ||
                activeLegend === 'best'
                  ? 1
                  : 0.4,
            }}
          >
            Team Best
          </span>
        </div>
      </div>

      {/* ======================================================
          CHART
      ====================================================== */}

      <div
        ref={chartRef}
        className="
          relative
          w-full
          select-none
          overscroll-contain
        "
        style={{
          overscrollBehavior: 'contain',
          touchAction: 'pan-x',
        }}
        onMouseLeave={() => {
          setActiveXAxisIndex(null);
        }}
      >
        <ResponsiveContainer
          width="100%"
          height={245}
        >
          <LineChart
            data={visibleData}
            margin={{
              top: 10,
              right: 15,
              left: 5,
              bottom: 48,
            }}
            onMouseMove={(
              state: any,
            ) => {
              const index =
                state?.activeTooltipIndex;

              if (
                index !== undefined &&
                index !== null &&
                Number.isFinite(
                  Number(index),
                )
              ) {
                setActiveXAxisIndex(
                  Number(index),
                );
              }
            }}
          >
            {/* ==================================================
                GRID
            ================================================== */}

            <CartesianGrid
              vertical={false}
              stroke="#EAECF0"
              strokeWidth={1}
            />

            {/* ==================================================
                X AXIS
            ================================================== */}

            <XAxis
              dataKey="day"
              interval={0}
              height={64}
              tickMargin={0}
              axisLine={{
                stroke: '#EAECF0',
                strokeWidth: 1,
              }}
              tickLine={false}
              tick={
                <CustomXAxisTick
                  activeIndex={
                    activeXAxisIndex
                  }
                />
              }
            />

            {/* ==================================================
                Y AXIS
            ================================================== */}

            <YAxis
              domain={[
                0,
                yAxisMax,
              ]}
              ticks={yAxisTicks}
              allowDecimals={false}
              tickFormatter={(value) =>
                `${value}h`
              }
              axisLine={false}
              tickLine={false}
              width={42}
              tick={{
                fontSize: 13,
                fill: '#667085',
                fontWeight: 500,
              }}
            />

            {/* ==================================================
                TOOLTIP
            ================================================== */}

            <Tooltip
              content={
                <CustomTooltip />
              }
              cursor={{
                stroke: '#98A2B3',
                strokeWidth: 1,
                strokeDasharray: '4 4',
              }}
              offset={10}
              wrapperStyle={{
                outline: 'none',
                zIndex: 100,
              }}
            />

            {/* ==================================================
                ME
            ================================================== */}

            <Line
              type="monotone"
              dataKey="me"
              name={`Me (${employeeName})`}
              stroke="#003F7D"
              strokeWidth={2.5}
              strokeOpacity={
                activeLegend === null ||
                activeLegend === 'me'
                  ? 1
                  : 0.25
              }
              dot={{
                r: 4,
                fill: '#003F7D',
                stroke: '#FFFFFF',
                strokeWidth: 1,
              }}
              activeDot={{
                r: 7,
                fill: '#003F7D',
                stroke: '#FFFFFF',
                strokeWidth: 2,
              }}
              isAnimationActive={false}
            />

            {/* ==================================================
                TEAM AVG
            ================================================== */}

            <Line
              type="monotone"
              dataKey="team"
              name="Team Avg"
              stroke="#98A2B3"
              strokeWidth={2}
              strokeDasharray="5 5"
              strokeOpacity={
                activeLegend === null ||
                activeLegend === 'team'
                  ? 1
                  : 0.25
              }
              dot={{
                r: 4,
                fill: '#98A2B3',
                stroke: '#FFFFFF',
                strokeWidth: 1,
              }}
              activeDot={{
                r: 7,
                fill: '#98A2B3',
                stroke: '#FFFFFF',
                strokeWidth: 2,
              }}
              isAnimationActive={false}
            />

            {/* ==================================================
                TEAM BEST
            ================================================== */}

            <Line
              type="monotone"
              dataKey="best"
              name="Team Best"
              stroke="#16A34A"
              strokeWidth={2}
              strokeDasharray="5 5"
              strokeOpacity={
                activeLegend === null ||
                activeLegend === 'best'
                  ? 0.9
                  : 0.2
              }
              dot={{
                r: 3,
                fill: '#16A34A',
              }}
              activeDot={{
                r: 6,
                fill: '#16A34A',
                stroke: '#FFFFFF',
                strokeWidth: 2,
              }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* ======================================================
          ZOOM MESSAGE
      ====================================================== */}

      <div
        className="
          mt-1
          text-center
        "
      >
        <span
          className="
            text-[10px]
            text-[#98A2B3]
          "
        >
          Scroll over the chart to zoom ·
          Page scrolling is disabled while
          using the chart
        </span>
      </div>
    </div>
  );
};

export default MyHoursVsTeamAverage;